import { useApi } from "./use-api.hook";
import AuthClientStore from "../services/auth-client.service";
import { ApiMethod } from "../models/api-types.type";
import { routes } from "../../assets/constants";
import { User } from "../models/user.model";
import { endSessionLocally, readSessionError } from "../services/device-session.service";
import { endSupportSessionLocally, isSupportRefusal } from "../services/support-session.service";
import { toast } from "sonner";
import i18n from "i18next";
import { notePlanResponse, notifyPlanLimit } from "../../components/design-system/plan/plan-limit-store";

/*
 * These variables are used to debounce the refreshTokens function
 */
let debouncedPromise: Promise<unknown> | null;
let debouncedResolve: (...args: unknown[]) => void;
let debouncedReject: (...args: unknown[]) => void;
let timeout: number;

/**
 * Convert a thrown `Response` (from the fetch wrapper) or other error into
 * a regular `Error` whose `.message` is the server-provided message. NestJS
 * error responses look like `{ message, error, statusCode }` where `message`
 * may be a string or an array of validation messages.
 */
async function normalizeRequestError(e: any): Promise<Error> {
    if (e instanceof Response) {
        let message = `Request failed (${e.status})`;
        let body: any = null;
        try {
            body = await e.json();
            if (body?.message) {
                message = Array.isArray(body.message) ? body.message.join(', ') : String(body.message);
            } else if (body?.error) {
                message = String(body.error);
            }
        } catch {
            // non-JSON body — keep the generic message
        }
        const err: any = new Error(message);
        err.status = e.status;
        err.body = body;
        return err;
    }
    if (e instanceof Error) return e;
    return new Error(typeof e === 'string' ? e : 'Unknown request error');
}

export const useAuthApi = () => {
    const { sendRequest, sendProtectedRequest } = useApi();

    const login = async (email: string, password: string) => {
        const response = await sendRequest(ApiMethod.POST, routes.auth.login, {
            email,
            password,
        });

        AuthClientStore.setAccessToken(response.access_token);
        AuthClientStore.setRefreshToken(response.refresh_token);

        return response;
    };

    const logout = () => {
        // In a support tab, only the support session goes: the super admin's own
        // sign-in is shared with their other tabs.
        if (AuthClientStore.isSupportSession()) {
            AuthClientStore.removeSupportToken();
            return;
        }
        AuthClientStore.removeAccessToken();
        AuthClientStore.removeRefreshToken();
    };

    /** Tell the API this device is signing out, so its seat is freed now. */
    const signout = async () => {
        // A support session holds no device seat to free.
        if (AuthClientStore.isSupportSession()) return;
        try {
            await sendProtectedRequest(ApiMethod.POST, routes.auth.signout);
        } catch {
            // Best effort: an expired token just means the session ends on its own.
        }
    };

    const refreshTokens = async () => {
        clearTimeout(timeout);
        if (!debouncedPromise) {
            debouncedPromise = new Promise((resolve, reject) => {
                debouncedResolve = resolve;
                debouncedReject = reject;
            });
        }

        //@ts-ignore
        timeout = setTimeout(() => {
            const executeLogic = async () => {
                const response = await sendProtectedRequest(
                    ApiMethod.POST,
                    routes.auth.refreshTokens,
                    undefined,
                    AuthClientStore.getRefreshToken() ? true : false,
                );
                if(response){
                    AuthClientStore.setAccessToken(response.access_token);
                    AuthClientStore.setRefreshToken(response.refresh_token);
                }
            };

            executeLogic().then(debouncedResolve).catch(async (e) => {
                // The API ended this device's session (signed in elsewhere, signed
                // out from the Devices page): sign out here and say why.
                const body = await readSessionError(e);
                if (body?.code === 'SESSION_ENDED') {
                    endSessionLocally(body.reason || 'missing');
                }
                debouncedReject(e);
            });

            debouncedPromise = null;
        }, 1000)

        return debouncedPromise;
    };

    const sendAuthGuardedRequest = async (
        userIsNotAuthenticatedCallback: () => void,
        method: ApiMethod,
        path: string,
        // eslint-disable-next-line
        body?: any,
        init?: RequestInit,
    ) => {
        try {
            const response = await sendProtectedRequest(method, path, body, undefined, init);
            // A report cut to the plan's history window says so; the notice on the page listens.
            notePlanResponse(response);
            return response;
        } catch (e: any) {
            // A support session can't be refreshed: a 401 means it is over.
            if (e?.status === 401 && AuthClientStore.isSupportSession()) {
                endSupportSessionLocally();
                throw await normalizeRequestError(e);
            }
            if (e?.status === 401) {
                try {
                    await refreshTokens();
                } catch (e) {
                    userIsNotAuthenticatedCallback();
                    logout();
                }
                return await sendProtectedRequest(method, path, body, undefined, init);
            }
            else if (e?.status === 403) {
                const err: any = await normalizeRequestError(e);
                const body = err?.body || {};
                // Plan gating (quota/feature) returns 403 with a business payload —
                // surface it to the caller so it can show "limit reached / upgrade",
                // instead of logging the vendor out (which is for genuine auth 403s).
                // Support may not do this (view only, or the vendor's own plan,
                // billing, team or devices): say so, and stay in the session.
                if (isSupportRefusal(body?.code)) {
                    toast.error(i18n.t(`SUPPORT_ACCESS.REFUSED.${body.code}`));
                    throw err;
                }
                const isPlanError =
                    body?.quotaType ||
                    body?.requiredFeatures ||
                    /quota exceeded|not available on your current plan|monthly limit/i.test(err?.message || '');
                if (isPlanError) {
                    // One dialog for every limit, whichever page hit it (docs/plg-free-plan.md).
                    // Feature-only refusals (`requiredFeatures`) stay with the page: it shows the callout.
                    notifyPlanLimit(err);
                    throw err;
                }
                logout();
                return;
            }
            // Re-throw 4xx/5xx with the parsed server message so callers can surface
            // it to the user instead of getting silent `undefined` returns.
            throw await normalizeRequestError(e);
        }
    };

    const me = (userIsNotAuthenticatedCallback: () => void) => {
        return sendAuthGuardedRequest(
            userIsNotAuthenticatedCallback,
            ApiMethod.GET,
            routes.auth.me,
        ) as Promise<User>;
    };

    return { login, logout, signout, me, sendAuthGuardedRequest, refreshTokens };
};